import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Users, Shield } from 'lucide-react'
import { AnimatedTabContent } from '@/components/ui/animated-tab-content'
import { api } from '@/lib/api-client'
import { extractItems } from '@/lib/response-utils'
import { SettingsUsersPage } from './settings-users-page'
import { SettingsRolesPage } from './settings-roles-page'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { User } from '@/features/auth/types'
import type { Role } from '@/features/roles/api'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import { useAuthentication } from '@/features/auth/AuthenticationContext'

export function SettingsSecurityPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const { actions: userActions } = useAuthorization()
    const { user } = useAuthentication()
    const isAdmin = user?.is_administrator === true

    const canView = (action: string) => isAdmin || userActions.has(action)

    const tabs = [
        { id: 'users' as const, label: 'User Management', icon: Users, requiredAction: 'view_all_users' },
        { id: 'roles' as const, label: 'Role Management', icon: Shield, requiredAction: 'view_actions' },
    ]

    const availableTabs = tabs.filter(tab => canView(tab.requiredAction))

    type TabId = typeof tabs[number]['id']
    const validTabIds = availableTabs.map(t => t.id)
    const tabParam = searchParams.get('tab') as TabId | null
    const activeTab: TabId = tabParam && validTabIds.includes(tabParam as any) ? tabParam : (validTabIds[0] ?? 'users')
    const setActiveTab = useCallback((tab: TabId) => {
        setSearchParams({ tab }, { replace: true })
    }, [setSearchParams])

    // Fetch counts
    const { data: usersResponse } = useQuery({
        queryKey: ['admin', 'users'],
        queryFn: () => api.users.list(),
    })

    const { data: rolesResponse } = useQuery({
        queryKey: ['roles'],
        queryFn: () => api.roles.list(),
    })

    const users = extractItems<User>(usersResponse)
    const roles: Role[] = rolesResponse?.data?.roles || []
    const totalUsers = Array.isArray(users) ? users.length : 0
    const totalRoles = roles.length

    const countMap: Record<string, number> = {
        'users': totalUsers,
        'roles': totalRoles,
    }

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
        >
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
            >
                <h1 className="text-2xl font-bold text-gray-900">Security</h1>
                <p className="text-gray-600">Manage users, roles, and permissions</p>
            </motion.div>

            {/* Tab Navigation — underline style matching other settings pages */}
            <motion.div
                className="border-b border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
            >
                <nav className="-mb-px flex space-x-8">
                    {availableTabs.map((tab) => {
                        const Icon = tab.icon
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Icon className="h-4 w-4 inline mr-2" />
                                {tab.label}
                                <span className="ml-1.5 text-xs font-normal rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">
                                    {countMap[tab.id] ?? 0}
                                </span>
                            </button>
                        )
                    })}
                </nav>
            </motion.div>

            {/* Tab Content */}
            <AnimatedTabContent activeTab={activeTab}>
                {activeTab === 'users' && <SettingsUsersPage />}
                {activeTab === 'roles' && <SettingsRolesPage />}
            </AnimatedTabContent>
        </motion.div>
    )
}
